import React from 'react';

interface LogoComponentProps {
  className?: string;
  size?: number;
  /**
   * Draw the mark in a single colour, taken from the surrounding text, instead
   * of the brand gradient. For places where the gradient competes with the
   * content rather than framing it — a documentation header sitting above a
   * page of prose, say — and where the mark should read as part of the
   * wordmark beside it.
   */
  monochrome?: boolean;
}

const LogoSvg: React.FC<LogoComponentProps> = ({
  size = 50,
  className,
  monochrome = false,
}) => {
  const paint = (index: number) =>
    monochrome ? 'currentColor' : `url(#paint${index}_linear_219_470)`;

  return (
    <div className={className}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 306 306"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M76.7647 306C84.0743 306 90 300.074 90 292.765C90 285.455 84.0743 279.529 76.7647 279.529H45C34.7665 279.529 26.4706 271.234 26.4706 261V229.235C26.4706 221.926 20.545 216 13.2353 216C5.92564 216 7.70006e-06 221.926 7.78722e-06 229.235L8.16601e-06 261C8.46238e-06 285.853 20.1472 306 45 306H76.7647Z"
          fill={paint(0)}
        />
        <path
          d="M76.7647 213C84.0743 213 90 207.074 90 199.765C90 192.455 84.0743 186.529 76.7647 186.529H45C34.7665 186.529 26.4706 178.234 26.4706 168V136.235C26.4706 128.926 20.545 123 13.2353 123C5.92564 123 7.70006e-06 128.926 7.78722e-06 136.235L8.16601e-06 168C8.46238e-06 192.853 20.1472 213 45 213H76.7647Z"
          fill={paint(1)}
        />
        <path
          d="M306 229.235C306 221.926 300.074 216 292.765 216C285.455 216 279.529 221.926 279.529 229.235V261C279.529 271.234 271.234 279.529 261 279.529H229.235C221.926 279.529 216 285.455 216 292.765C216 300.074 221.926 306 229.235 306H261C285.853 306 306 285.853 306 261V229.235Z"
          fill={paint(2)}
        />
        <path
          d="M306 136.235C306 128.926 300.074 123 292.765 123C285.455 123 279.529 128.926 279.529 136.235V168C279.529 178.234 271.234 186.529 261 186.529H229.235C221.926 186.529 216 192.455 216 199.765C216 207.074 221.926 213 229.235 213H261C285.853 213 306 192.853 306 168V136.235Z"
          fill={paint(3)}
        />
        <path
          d="M42.5001 25C32.8351 25 25.0001 32.835 25.0001 42.5V73.5C25.0001 73.5149 25.0001 73.5297 25 73.5446V77.5C25 87.165 32.835 95 42.5 95H77.5C84.4036 95 90 100.596 90 107.5C90 114.404 84.4036 120 77.5 120H42.5C19.0279 120 -1.97855e-06 100.972 0 77.5L2.52882e-06 46.5C2.53005e-06 46.4853 2.777e-05 46.4707 7.82075e-05 46.456V42.5C7.82075e-05 19.0279 19.028 0 42.5001 0H77.5001C84.4036 0 90.0001 5.59644 90.0001 12.5C90.0001 19.4036 84.4036 25 77.5001 25H42.5001Z"
          fill={paint(4)}
        />
        <path
          d="M263.5 95C273.165 95 281 87.165 281 77.5V46.5C281 46.4851 281 46.4703 281 46.4554V42.5C281 32.835 273.165 25 263.5 25L228.5 25C221.596 25 216 19.4036 216 12.5C216 5.59645 221.596 1.55094e-05 228.5 1.61129e-05L263.5 1.91727e-05C286.972 2.88541e-05 306 19.0279 306 42.5V73.5C306 73.5147 306 73.5293 306 73.544V77.5C306 100.972 286.972 120 263.5 120L228.5 120C221.596 120 216 114.404 216 107.5C216 100.596 221.596 95 228.5 95L263.5 95Z"
          fill={paint(5)}
        />
        <path
          d="M118 263.5C118 273.165 125.835 281 135.5 281H166.5C166.515 281 166.53 281 166.545 281H170.5C180.165 281 188 273.165 188 263.5V228.5C188 221.596 193.596 216 200.5 216C207.404 216 213 221.596 213 228.5V263.5C213 286.972 193.972 306 170.5 306H139.5C139.485 306 139.471 306 139.456 306H135.5C112.028 306 93 286.972 93 263.5V228.5C93 221.596 98.5964 216 105.5 216C112.404 216 118 221.596 118 228.5L118 263.5Z"
          fill={paint(6)}
        />
        <path
          d="M118 170.5C118 180.165 125.835 188 135.5 188H166.5C166.515 188 166.53 188 166.545 188H170.5C180.165 188 188 180.165 188 170.5V135.5C188 128.596 193.596 123 200.5 123C207.404 123 213 128.596 213 135.5V170.5C213 193.972 193.972 213 170.5 213H139.5C139.485 213 139.471 213 139.456 213H135.5C112.028 213 93 193.972 93 170.5V135.5C93 128.596 98.5964 123 105.5 123C112.404 123 118 128.596 118 135.5L118 170.5Z"
          fill={paint(7)}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M170.5 0C193.972 0 213 19.0279 213 42.5V73.5C213 73.5132 213 73.5263 213 73.5395V77.5C213 100.972 193.972 120 170.5 120H136.5C136.495 120 136.491 120 136.486 120H135.5C112.028 120 93 100.972 93 77.5L93 42.5C93 19.0279 112.028 -1.96721e-06 135.5 0H170.5ZM170.5 95C180.165 95 188 87.165 188 77.5V46.5C188 46.4868 188 46.4737 188 46.4605V42.5C188 32.835 180.165 25 170.5 25L135.5 25C125.835 25 118 32.835 118 42.5L118 77.5C118 87.165 125.835 95 135.5 95H170.5Z"
          fill={paint(8)}
        />
        {monochrome ? null : (
          <defs>
            <linearGradient
              id="paint0_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
            <linearGradient
              id="paint3_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
            <linearGradient
              id="paint4_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
            <linearGradient
              id="paint5_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
            <linearGradient
              id="paint6_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
            <linearGradient
              id="paint7_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
            <linearGradient
              id="paint8_linear_219_470"
              x1="292.5"
              y1="12.5"
              x2="-42.2647"
              y2="159.029"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#975ED7" />
              <stop offset="1" stopColor="#F7505A" />
            </linearGradient>
          </defs>
        )}
      </svg>
    </div>
  );
};

export default LogoSvg;
